// src/components/ThemeSelector.jsx
import { useState, useRef, useEffect } from "react";
import { useTheme } from "../contexts/ThemeProvider";
import "../components/styles/ThemeSelector.css";

/**
 * ThemeSelector
 * -------------
 * A self-contained floating button + dropdown.
 * Drop it anywhere in your layout — it renders fixed/absolute independently.
 *
 * Usage:
 *   import ThemeSelector from "./components/ThemeSelector";
 *   <ThemeSelector />
 *
 * Optional props:
 *   position="bottom-right"  | "bottom-left" | "top-right" | "top-left"
 */
export default function ThemeSelector({ position = "bottom-right" }) {
  const { themes, activeTheme, applyTheme, loading } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  /* Close on outside click */
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (loading || themes.length === 0) return null;

  return (
    <div className={`ts-root ts-root--${position}`} ref={ref}>
      {/* Floating trigger */}
      <button
        className="ts-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-label="Change colour theme"
        title="Change theme"
      >
        <PaletteIcon />
        {activeTheme && (
          <span className="ts-trigger-dots">
            {activeTheme.preview.map((c, i) => (
              <span key={i} className="ts-dot" style={{ background: c }} />
            ))}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="ts-panel" role="menu" aria-label="Theme picker">
          <div className="ts-panel-header">
            <span className="ts-panel-title">Colour Theme</span>
            <button className="ts-close" onClick={() => setOpen(false)} aria-label="Close">
              <CloseIcon />
            </button>
          </div>

          <div className="ts-grid">
            {themes.map((theme) => {
              const isActive = activeTheme?.id === theme.id;
              return (
                <button
                  key={theme.id}
                  className={`ts-card ${isActive ? "ts-card--active" : ""}`}
                  onClick={() => { applyTheme(theme.id); setOpen(false); }}
                  role="menuitem"
                  aria-pressed={isActive}
                  title={theme.description}
                >
                  {/* Swatch row */}
                  <span className="ts-swatches">
                    {theme.preview.map((c, i) => (
                      <span key={i} className="ts-swatch" style={{ background: c }} />
                    ))}
                  </span>
                  <span className="ts-card-name">{theme.name}</span>
                  <span className="ts-card-desc">{theme.description}</span>
                  {isActive && <span className="ts-checkmark"><CheckIcon /></span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Inline SVG icons (no external dep) ── */
function PaletteIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
      <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
      <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
      <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
